import Section from 'components/Section';
import React from 'react';
import DataGridTable from '.';
import { User, UserStatusEnum } from 'types';
import { Link } from 'react-router-dom';
import SummarizeIcon from '@mui/icons-material/Summarize';

import { getEnumKeyFromValue, getSelectedObjectiveGoalsYear } from 'utils/common';
import {
  DOWNLOAD_EVALUATION_REPORTS,
  DownloadEvaluationReportsResponse,
  DownloadEvaluationReportsVariables,
} from 'graphql/query/user';
import { useLazyQuery } from '@apollo/client';
import { downloadBinaryData } from 'utils/admin';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';

const UsersTable: React.FC<{ rows: User[]; title: string }> = ({ rows, title }) => {
  const [downloadEvaluationReport, { loading: downloadingEvaluationReport }] = useLazyQuery<
    DownloadEvaluationReportsResponse,
    DownloadEvaluationReportsVariables
  >(DOWNLOAD_EVALUATION_REPORTS, {
    fetchPolicy: 'network-only',
  });

  const handleReportDownload = () => {
    downloadEvaluationReport({
      variables: {
        year: getSelectedObjectiveGoalsYear(),
        users: rows.map(row => {
          return row._id;
        }),
      },
      onCompleted: res =>
        downloadBinaryData({
          content: res.downloadEvaluationReports.file,
          filename: res.downloadEvaluationReports.fileName,
        }),
    });
  };

  return (
    <Section
      title={title}
      collapsible
      action={
        <Tooltip title="Download Objectives">
          <IconButton sx={{ color: 'white' }}>
            {downloadingEvaluationReport ? (
              <CircularProgress size={21} color="inherit" />
            ) : (
              <SummarizeIcon color="inherit" onClick={handleReportDownload} />
            )}
          </IconButton>
        </Tooltip>
      }
    >
      <DataGridTable
        sx={{
          maxWidth: '78vw',
          mx: 'auto',
          minHeight: '10vh',
        }}
        getRowId={row => row._id}
        columns={[
          {
            field: 'empId',
            headerName: 'Emp ID',
            minWidth: 100,
            renderCell: params => (
              <Link to={`/team/${params.row._id}?name=${params.row.name}`}>{params.row.empId}</Link>
            ),
          },
          { field: 'name', headerName: 'Name', minWidth: 100, flex: 1 },
          { field: 'designation', headerName: 'Designation', minWidth: 150 },
          { field: 'email', headerName: 'Email', minWidth: 150, flex: 1 },
          {
            field: 'annualUserInfo1',
            headerName: 'Objective Settings Status',
            minWidth: 220,
            valueFormatter: (_, row: User) =>
              getEnumKeyFromValue(UserStatusEnum, row.annualUserInfo?.startYearStatus),
          },
          {
            field: 'annualUserInfo2',
            headerName: 'Mid Year Status',
            minWidth: 200,
            valueFormatter: (_, row: User) =>
              getEnumKeyFromValue(UserStatusEnum, row.annualUserInfo?.midYearStatus),
          },
          {
            field: 'annualUserInfo3',
            headerName: 'End Year Status',
            minWidth: 200,
            valueFormatter: (_, row: User) =>
              getEnumKeyFromValue(UserStatusEnum, row.annualUserInfo?.endYearStatus),
          },
        ]}
        rows={rows}
      />
    </Section>
  );
};

export default UsersTable;
